import { Injectable } from "@angular/core";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { StringHelper } from "@tylertech/shared-module";
import { Tuple } from "@tylertech/tyler-api-types";

export class Cache<T>
{
    private readonly log = getLoggerFor("Cache", () => `[Cache '${this.name ?? "root"}']`);
    protected readonly _cache = new Map<string, T>();

    constructor(
        private readonly name?: string,
        private readonly parent?: Cache<Cache<T>>,
        readonly checkPointId?: string)
    { }

    get length(): number
    {
        return this._cache.size;
    }

    /**
     * If a key is present, only the value(s) for the key will be cleared;
     * otherwise this entire cache will be cleared.
     * @param key Cache Key to clear.
     */
    @LogInvocation(LogLevel.DEBUG)
    clear(key?: string): void
    {
        if (key === undefined || key === null)
        {
            this._cache.clear();
        }
        else if (this._cache.has(key))
        {
            this._cache.delete(key);
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    set(key: string, value: T): T
    {
        this._cache.set(key, value);
        return value;
    }

    @LogInvocation(LogLevel.DEBUG)
    get(key: string): T
    {
        return this._cache.get(key);
    }

    @LogInvocation(LogLevel.DEBUG)
    forEach(fn: (key: string, value: T) => void): void
    {
        if (!fn)
        {
            throw new Error(`Cache.forEach must be given a call function!`);
        }

        let index = 0;
        this._cache.forEach((value, key) =>
        {
            this.log.debug(`Cache.forEach[${index++}] | `, { key, value });
            fn(key, value);
        });
    }

    toString(): string
    {
        return StringHelper.stringify(this.toJSON());
    }

    toJSON(): unknown
    {
        return [
            ...this._cache.entries()
        ].reduce(
            (o: Record<string, unknown>, [k, v]: [string, T]) =>
            {
                o[k] = hasToJson(v) ? v.toJSON() : v;

                return o;
            },
            {});
    }
}

function hasToJson<T = unknown>(v: T): v is T & { toJSON(): unknown; }
{
    return v && typeof v === "object" && "toJSON" in v;
}

@Injectable()
export class CacheHelperService
{
    private readonly log = getLoggerFor("CacheHelperService");

    /**
     * Key for outermost Cache is the CallName; i.e., "ServiceName/MethodName" or whatever.
     * Key for middle Cache is assumed to be typeName; i.e., "Payroll.Model.PayrollSet".
     * Key for innermost Cache is assumed to be unique string for call; i.e., an OData filterString, or entity Id.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected _cacheCache: Cache<Cache<Cache<any>>>;

    private static _checkpointProviderId: string;

    constructor()
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this._cacheCache = new Cache<Cache<Cache<any>>>();
    }

    /**
     * Clear all caches where the requestContext matches the given type and secondKey.
     *
     * @param type Type name
     * @param secondKey specific identifier for a request type.
     */
    @LogInvocation(LogLevel.DEBUG)
    clear(type: string, secondKey: string): void
    {
        this._cacheCache.forEach(
            (_callerKey, callerCache) =>
            {
                callerCache.forEach(
                    (typeKey: string, typeCache: Cache<unknown>) =>
                    {
                        if (typeKey === type)
                        {
                            typeCache.clear(secondKey);
                        }
                    });
            });
    }

    /**
     * Clear all caches.
     */
    @LogInvocation(LogLevel.DEBUG)
    clearAll(): void
    {
        this._cacheCache = new Cache<Cache<Cache<unknown>>>();
    }

    /**
     * Clear all caches keyed with the given type.
     *
     * @param type Type name
     */
    @LogInvocation(LogLevel.DEBUG)
    clearAllForType(type: string): void
    {
        this._cacheCache.forEach(clearType);
        function clearType(_callerKey: string, value: Cache<Cache<unknown>>): void
        {
            value.clear(type);
        }
    }

    clearEditFormCache(providerId: string): void
    {
        this._cacheCache.forEach(
            (_callerKey, callerCache) =>
            {
                callerCache.forEach(
                    (typeKey: string, typeCache: Cache<unknown>) =>
                    {
                        if (typeCache.checkPointId === providerId)
                            typeCache.clear();
                    });
            });
    }

    static SetEditFormCacheCheckpointId(providerId: string): void
    {
        this._checkpointProviderId = providerId;
    }

    static ClearEditFormCacheCheckpointId(): void
    {
        this._checkpointProviderId = undefined;
    }

    protected static getCache<T>(cache: Cache<Cache<T>>, firstKey: string): Cache<T>
    {
        let typeCache = cache.get(firstKey);
        if (!typeCache)
        {
            typeCache = cache.set(firstKey, new Cache<T>(firstKey, cache, this._checkpointProviderId));
        }
        return typeCache;
    }

    protected static getCachedValues<T>(cache: Cache<Cache<T>>, firstKey: string, secondKey?: string): T
    {
        const typeCache = this.getCache(cache, firstKey);
        return typeCache.get(secondKey || "");
    }

    /**
     * Check with the cache to see if item is already present
     * @param callName	Some key identifying the caller context; i.e. "ServiceName/MethodName"
     * @param requestContext	Tuple containing the Type name and identifier for specific requests.
     */
    @LogInvocation(LogLevel.DEBUG)
    isInCache<T>(callName: string, params: Tuple<string, string>): boolean
    {
        const cacheByCallName = CacheHelperService.getCache<Cache<T>>(this._cacheCache, callName);
        const type = params.Item1;
        const id = params.Item2 || "";
        const values = CacheHelperService.getCachedValues(cacheByCallName, type, id);
        if (values) return true;
        return false;
    }

    /**
      * Check with the cache to see if we can already fullfil this request, other return to callFunction.
      * @param callName Some key identifying the caller context; i.e. "ServiceName/MethodName"
      * @param requestContext Tuple containing the Type name and identifier for specific requests.
      * @param callFunction Function to forward cache misses to.
      */
    @LogInvocation(LogLevel.DEBUG)
    routeThroughCache<T>(
        callName: string,
        params: Tuple<string, string>,
        callFunction: (params: Tuple<string, string>) => Promise<T>
    ): Promise<T>
    {
        const cacheByCallName = CacheHelperService.getCache<Cache<T>>(this._cacheCache, callName);
        const type = params.Item1;
        const id = params.Item2 || "";
        const values = CacheHelperService.getCachedValues(cacheByCallName, type, id);

        if (values)
        {
            return Promise.resolve(values);
        }

        if (!callFunction)
        {
            return null;
        }

        const thePromise = callFunction(params)
            .then(cacheResult, doNotCacheErrors);

        /**
         * Caching the promise, so that any other calls that match a given type and id
         * will not result in another http request. When the promise is resolved, the cache
         * will be updated with the actual values.
         */
        CacheHelperService.getCache<T | Promise<T>>(cacheByCallName, type)
            .set(id, thePromise);

        return thePromise;

        function cacheResult(result: T): T
        {
            return CacheHelperService.getCache<T>(cacheByCallName, type)
                .set(id, result);
        }

        function doNotCacheErrors(err: unknown): Promise<T>
        {
            // forget the cached promise... is bad...
            CacheHelperService.getCache<T>(cacheByCallName, type)
                .clear(id);

            throw err;
        }
    }
}
